import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import {
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import type { UpdateCompanyPartnerRequest } from '../../../api/dashboardAPI';
import CallToActionButton from '../../../common/CallToActionButton';
import { ReactComponent as Step1Icon } from '../../../assets/images/step1_icon.svg';
import { ReactComponent as Step2Icon } from '../../../assets/images/step2_icon.svg';
import { ReactComponent as Step3Icon } from '../../../assets/images/step3_icon.svg';
import { ReactComponent as HomedepotLogo } from '../../../assets/images/homedepot_logo.svg';
import { PostponeHdSetup } from './common/PostponeHdStep';
import { OnboardHDShopQuestion } from './OnboardHDShopQuestion';
import OnboardingLayout from './common/OnboardingLayout';
import { useSolutionOrg } from '../../../config/useSolutionOrg';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { OnboardingStepper } from '../../../common/OnboardingStepper';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setFromHDToEdgeReturnPath, selectMemberInformation } from '../../../store/slices/onboardingSlice';
import { updateCompanyPartner } from '../../../store/thunks/dashboardThunk';
import { useOnboardingRedirectPath } from '../../../common/useOnboardingRedirectPath';
import { isUserInitiatedHomeDepotEnrollment } from '../../../common/homeDepotEnrollmentIntent';
import { PartnerEnrollmentStatus } from '../../../common/enums';
import { Logger } from '../../../config/logging/logger';
import { TestIds } from '../../../assets/testIds';

export function OnboardHDHome() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { isNativeAppExperience } = useSolutionOrg();
  const { trackPartnerEnrollment } = useCustomEventTracking();
  const { company } = useAppSelector(selectMemberInformation);
  const logger = Logger.create('OnboardHDHome');
  const location = useLocation();
  const userInitiatedHdEnrollment = isUserInitiatedHomeDepotEnrollment({
    state: location.state,
    search: location.search,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showHDShopQuestion, setShowHDShopQuestion] =
    useState(!userInitiatedHdEnrollment);

  // get redirect path
  const redirectPath = useOnboardingRedirectPath({
    redirectToPartnersForOnboardingFlowTestOff: true,
  });

  useEffect(() => {
    setShowHDShopQuestion(!userInitiatedHdEnrollment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToHomeDepot = () => {
    trackPartnerEnrollment('Click', 'MobileContinueToHomeDepotSetup', {
      isOptimizelyEvent: true,
    });

    // Mark the relation as `action_required` the moment we hand the member off to the
    // Home Depot site. Every later status write (`in_progress`, `completed`) happens at
    // the very end of the Joist script flow, so a member who taps through, signs in and
    // then abandons would otherwise stay `not_started` forever. Fire-and-forget: the
    // handoff must not wait on (or be blocked by) this call.
    const request: UpdateCompanyPartnerRequest = {
      enrollmentStatus: PartnerEnrollmentStatus.ActionRequired,
    };
    dispatch(updateCompanyPartner({
      companyId: company?.id || '',
      partnerId: process.env.REACT_APP_HOME_DEPOT_PARTNER_ID || '',
      data: request,
    }))
      .then(unwrapResult)
      .catch((error: unknown) => {
        logger.error(Logger.format(error));
      });

    dispatch(setFromHDToEdgeReturnPath(redirectPath));
    window.parent.postMessage({
      payload: { hideOptimusApp: true },
      type: 'OPTIMUS_HIDE_REQUEST',
    }, '*');
  };

  return (
    <OnboardingLayout
      customStepper={(
        !userInitiatedHdEnrollment &&
        showHDShopQuestion
      ) ?
        <Box sx={{
          maxWidth: theme.spacing(53.75),
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3),
          marginTop: theme.spacing(3),
          marginBottom: theme.spacing(0),
        }}>
          <OnboardingStepper
            stepLabels={['Step 1', 'Step 2']}
            activeStep={1}
          />
        </Box> : undefined
      }
      reduceMobileTopPadding={isNativeAppExperience}
    >
      <Box>
        <Box>
          {/* <Typography sx={{
        // mt: theme.spacing(2),
        textAlign: 'center',
        paddingRight: theme.spacing(5),
        paddingLeft: theme.spacing(5),
      }}>{t('home.headingEdge')}</Typography> */}
          <Box
            sx={{
              width: '100%',
              mt: theme.spacing(0.25),
            }}
          >
            <Stack
              spacing={0}
              sx={() => ({
                borderRadius: theme.spacing(1),
                backgroundColor: theme.palette.common.white,
                padding: theme.spacing(2),
                pb: showHDShopQuestion ? theme.spacing(2) : theme.spacing(0),
                marginTop: showHDShopQuestion ? theme.spacing(2) : theme.spacing(0),
              })}
            >
              {showHDShopQuestion && <Box sx={{
                mt: theme.spacing(2.5),
              }}>
                <Typography variant="h2"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.grey[800],
                  }}
                >
                  {t('hdSetUpWeb.heading1')}
                </Typography>
              </Box>}
              {showHDShopQuestion && <Typography sx={{
                textAlign: 'left',
                color: theme.palette.brand.primary,
                mt: theme.spacing(2.5),
                mb: theme.spacing(2.5),
              }} >
                {t('hdSetUpWeb.heading2')}
              </Typography>}
              {showHDShopQuestion ?
                <OnboardHDShopQuestion setShowHDShopQuestion={setShowHDShopQuestion} /> :
                <>
                  <Box sx={{ display: 'flex', mt: theme.spacing(2.5) }}>
                    <Box>
                      <HomedepotLogo width={'106px'} height={'auto'} />
                    </Box>
                    <Box sx={{ marginLeft: '15px', textAlign: 'left' }}>
                      <Typography variant='body4'>{t('home.descriptionEdge1')}</Typography>
                      <Typography variant='body4'>{t('home.descriptionEdge2')}</Typography>
                      <Typography sx={{ marginTop: '0px' }}>{t('home.descriptionEdge3')}</Typography>
                    </Box>
                  </Box>
                  <List sx={{
                    width: '100%', maxWidth: 360, bgcolor: 'background.paper', pb: 0,
                  }}>
                    <ListItem sx={{
                      border: '1px solid #DADADA',
                      borderRadius: theme.spacing(1.625),
                    }}>
                      <ListItemAvatar>
                        <Step1Icon />
                      </ListItemAvatar>
                      <ListItemText primary={<div style={{ fontSize: '16px', marginTop: '0px', marginBottom: '0px' }}><div style={{ fontSize: '11px', color: theme.palette.grey[600], fontWeight: 700 }}>{t('home.step1.heading')}</div><div><span style={{ fontWeight: '600' }}>{t('home.step1.description1')}</span> {t('home.step1.description2')} <span style={{ fontWeight: '600' }}>{t('home.step1.description3')}</span> {t('home.step1.description4')}</div> <div style={{ fontSize: '14px', color: theme.palette.grey[600], fontWeight: 400 }}>{t('home.step1.description5')}</div></div>} />
                    </ListItem>
                    <ListItem sx={{
                      border: '1px solid #DADADA',
                      borderRadius: theme.spacing(1.625),
                      marginTop: '8px',
                    }}>
                      <ListItemAvatar>
                        <Step2Icon />
                      </ListItemAvatar>
                      <ListItemText primary={<div style={{ fontSize: '16px', marginTop: '0px', marginBottom: '0px' }}><div style={{ fontSize: '11px', color: theme.palette.grey[600], fontWeight: 700 }}>{t('home.step2.heading')}</div><div style={{ fontWeight: '600' }}>{t('home.step2.description1')}</div> <div style={{ fontSize: '14px', color: theme.palette.grey[600], fontWeight: 400 }}>{t('home.step2.description2')}</div></div>} />
                    </ListItem>
                    <ListItem sx={{
                      border: '1px solid #DADADA',
                      borderRadius: theme.spacing(1.625),
                      marginTop: '8px',
                    }}>
                      <ListItemAvatar>
                        <Step3Icon />
                      </ListItemAvatar>
                      <ListItemText primary={<div style={{ fontSize: '16px', marginTop: '0px', marginBottom: '0px' }}><div style={{ fontSize: '11px', color: theme.palette.grey[600], fontWeight: 700 }}>{t('home.step3.heading')}</div><div><span style={{ fontWeight: '600' }}>{t('home.step3.descriptionEdge1')}</span> {t('home.step3.descriptionEdge2')} <span style={{ fontWeight: '600' }}>{t('home.step3.descriptionEdge2b')}</span></div> <div style={{ fontSize: '14px', color: theme.palette.grey[600], fontWeight: 400 }}>{t('home.step3.descriptionEdge3')}</div></div>} />
                    </ListItem>
                  </List>
                  <Box>
                    <CallToActionButton
                      text={t('button.completeSetup') as string}
                      onBtnClick={navigateToHomeDepot}
                      data-testid={TestIds.onboarding.homedepotMobileCompleteSetupBtn}
                      sx={() => ({
                        position: 'static',
                        width: '100%',
                        marginTop: theme.spacing(2.5),
                        marginBottom: theme.spacing(1),
                        background: theme.palette.brand.primary,
                        borderRadius: theme.spacing(1.5),
                        '&:hover': {
                          backgroundColor: theme.palette.brand.primary,
                        },
                        [theme.breakpoints.up('sm')]: {
                          width: '100%',
                          marginLeft: '0px',
                        },
                      })}
                    />
                  </Box>
                </>
              }
            </Stack>
            {!showHDShopQuestion && <PostponeHdSetup />}
          </Box>
        </Box>
      </Box>
    </OnboardingLayout>
  );
}
